/**
 * Email composable
 * Sends emails by adding documents to the 'Emails' collection
 * Works with Firebase Extensions "Trigger Email" or similar background processors
 */
import { ref } from 'vue'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { getFirebaseDb } from '@/core/firebase/firebase'

/**
 * Composable for sending emails via Firestore
 * @returns {Object} Email methods and state
 */
export function useEmail() {
  const sending = ref(false)
  const error = ref(null)

  /**
   * Send an email by adding a document to the Emails collection
   * @param {Object} options - Email options
   * @param {string|string[]} options.to - Recipient email(s)
   * @param {string} options.subject - Email subject
   * @param {string} options.html - Email HTML content
   * @param {string} options.from - Sender name (optional)
   * @param {Array} options.attachments - Array of attachment objects (optional)
   * @param {string} options.attachments[].filename - Attachment filename
   * @param {string} [options.attachments[].content] - Base64 encoded content (inline; counts toward Firestore's 1MiB doc limit)
   * @param {string} [options.attachments[].path] - URL (e.g. Storage download URL) fetched at send time; use for large attachments
   * @returns {Promise<string>} Document ID of the created email
   */
  async function sendEmail({ to, cc, subject, html, from = 'GoFood', attachments = [] }) {
    sending.value = true
    error.value = null

    try {
      const db = getFirebaseDb()
      const emailsRef = collection(db, 'Emails')

      // Normalize 'to' to array and replace test email
      const recipients = (Array.isArray(to) ? to : [to]).map((addr) =>
        addr === 'test@test.com' ? 'yavrosman@gmail.com' : addr
      )

      const ccList = cc
        ? (Array.isArray(cc) ? cc : [cc]).filter((addr) => typeof addr === 'string' && addr.trim())
        : []

      const message = {
        subject,
        html,
      }

      // Add attachments if provided. Two forms:
      //  - { filename, content } — inline base64, stored in the Firestore doc
      //  - { filename, path }    — URL fetched by the email extension at send
      //    time; use for large/unbounded attachments (PDFs etc.)
      if (attachments && attachments.length > 0) {
        message.attachments = attachments.map((att) =>
          att.path
            ? { filename: att.filename, path: att.path }
            : { filename: att.filename, content: att.content, encoding: 'base64' }
        )
      }

      // Firestore hard-caps documents at 1,048,576 bytes — fail fast with a
      // clear error instead of an opaque FirebaseError from addDoc
      const estimatedSize =
        (html?.length || 0) +
        (attachments || []).reduce((sum, att) => sum + (att.content?.length || 0), 0)
      if (estimatedSize > 1_000_000) {
        throw new Error(
          `Email payload too large for Firestore (~${Math.round(estimatedSize / 1024)}KB). ` +
            'Upload attachments to Storage and pass { filename, path: url } instead of inline content.'
        )
      }

      const emailDoc = {
        to: recipients,
        from,
        message,
        createdAt: serverTimestamp(),
        status: 'pending',
      }

      if (ccList.length > 0) {
        emailDoc.cc = ccList
      }

      const docRef = await addDoc(emailsRef, emailDoc)
      return docRef.id
    } catch (err) {
      console.error('Email send error:', err)
      error.value = err.message
      throw err
    } finally {
      sending.value = false
    }
  }

  /**
   * Send email to multiple recipients with same content
   * @param {string[]} recipients - Array of email addresses
   * @param {string} subject - Email subject
   * @param {string} html - Email HTML content
   * @param {string} from - Sender name (optional)
   * @returns {Promise<string>} Document ID
   */
  async function sendBulkEmail(recipients, subject, html, from = 'GoFood') {
    return sendEmail({ to: recipients, subject, html, from })
  }

  return {
    sending,
    error,
    sendEmail,
    sendBulkEmail,
  }
}
